import { computed, inject, Injectable, signal, WritableSignal } from "@angular/core";
import { rxResource, toSignal } from "@angular/core/rxjs-interop";
import { debounceTime, filter, map, of, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { DemographicAreaData, InhabitantDemographicsModel, QueryLimit } from "src/models/map/demographic-area-data";
import { HttpDataService, IResponseWrapper } from "../httpdata.service";
import { IMapUpdate, MapStateService } from "../map/map-state.service";
import { MarketSalesStateService } from "./market-sales-state.service";
import { StatisticPanes } from "src/models/map/statistic-panes.enum";

@Injectable({
    providedIn: "root",
})
export class DemographyDataService {
    private readonly http = inject(HttpDataService);
    private readonly mapStateService = inject(MapStateService);
    private readonly marketSalesStateService = inject(MarketSalesStateService);

    private minSalesZoom = 12;
    private initHasRun: boolean = false;
    pursuantTransitionYears: WritableSignal<number> = signal(null);

    inhabitantDemographicUrl = `${environment.apiUrl}/Demographics/SearchAreaInhabitantDemographics`;
    demographicsUrl = `${environment.apiUrl}/Demographics/SearchBuyerDemographics`;

    private readonly mapUpdate = toSignal(
        this.mapStateService.mapUpdateBoundsOrPolygons$.pipe(
            filter((v) => this.filterNoPolygonObs(v)),
            tap(() => { this.initHasRun = true; }),
            debounceTime(150),
        ),
    );
    private readonly mapPolygons = toSignal(this.mapStateService.mapPolygonsChange$);
    private readonly mapUpdateRaw = toSignal(this.mapStateService.mapUpdateBoundsOrPolygons$);

    inhabitantDemographicParams = computed(() => ({
        areas: this.marketSalesStateService.filterFormAPI().areas,
        polygons: this.marketSalesStateService.filterFormAPI().geoBorder,
    }));

    inhabitantDemographicResource = rxResource({
        params: () => {
            const mapState = this.mapUpdate();
            if (!mapState) return null;
            return {
                filterParams: this.inhabitantDemographicParams(),
                mapState,
                polygons: this.mapPolygons(),
                pane: this.mapStateService.currentPane(),
            };
        },
        stream: ({ params }) => {
            if (!params) return of([] as any);
            const { filterParams, mapState, polygons, pane } = params;
            if (filterParams && mapState.state.zoom < this.minSalesZoom && polygons == null) {
                return of([] as any);
            } else if (pane !== StatisticPanes.demographyInhabitants) {
                return of([] as any);
            } else {
                return this.http.post<IResponseWrapper<InhabitantDemographicsModel[]>>(this.inhabitantDemographicUrl, filterParams).pipe(map((res) => res.data));
            }
        },
    });

    inhabitantDemographicIsLoading = this.inhabitantDemographicResource.isLoading;

    inhabitantDemographicVisibleSection = computed(() => {
        const isLoading = this.inhabitantDemographicIsLoading();
        const update = this.mapUpdateRaw();
        if (isLoading) return VisibleSection.showData;
        if (!update) return VisibleSection.showData;
        if ((update.polygons == null || update.polygons.length == 0) && update.state.zoom < 11) {
            return VisibleSection.zoomIn;
        }
        return VisibleSection.showData;
    });

    demographicParams = computed(() => ({
        polygons: this.marketSalesStateService.filterFormAPI().geoBorder,
        mapMode: this.mapStateService.mapModeSignal(),
        pursuantTransitionYears: this.pursuantTransitionYears(),
        ...this.marketSalesStateService.filterFormAPI(),
    }));

    demographicResource = rxResource({
        params: () => {
            const mapState = this.mapUpdate();
            if (!mapState) return null;
            return {
                filterParams: this.demographicParams(),
                mapState,
                polygons: this.mapPolygons(),
                pane: this.mapStateService.currentPane(),
            };
        },
        stream: ({ params }) => {
            if (!params) return of({} as any);
            const { filterParams, mapState, polygons, pane } = params;
            if (filterParams && mapState.state.zoom < this.minSalesZoom && polygons == null && pane !== StatisticPanes.demographyBuyers) {
                return of({} as any);
            } else if (pane !== StatisticPanes.demographyBuyers) {
                return of({} as any);
            } else {
                return this.http.post<IResponseWrapper<DemographicAreaData>>(this.demographicsUrl, filterParams).pipe(map((res) => res.data));
            }
        },
    });

    demographicsIsLoading = this.demographicResource.isLoading;

    demographicsVisibleSection = computed(() => {
        const isLoading = this.demographicsIsLoading();
        const demography = this.demographicResource.value();
        const update = this.mapUpdateRaw();
        if (isLoading) return VisibleSection.nothing;
        if (!update) return VisibleSection.showData;
        if ((update.polygons == null || update.polygons.length == 0) && update.state.zoom < 11) {
            return VisibleSection.zoomIn;
        }
        if (demography == null) return VisibleSection.noData;
        if (demography.queryLimitReached == QueryLimit.LimitReached) return VisibleSection.limitReached;
        if (demography.isMixedEstates) return VisibleSection.showMixedEstatesWarning;
        if (demography.numEstates === 0) return VisibleSection.noData;
        return VisibleSection.showData;
    });

    private filterNoPolygonObs(value: IMapUpdate): boolean {
        const hasNoPolygon = value.polygons == null || value.polygons.length === 0;
        return hasNoPolygon || !this.initHasRun;
    }
}

export enum VisibleSection {
    zoomIn = 1,
    showData = 2,
    noData = 3,
    showMixedEstatesWarning = 4,
    nothing = 5,
    limitReached = 6
}
